<template>
  <HgAccordion
    :id="id"
    class="hide-on-print"
    :themeClass="isPlatformIs24() ? $style.IS24AccordionThemeClass : $style.accordionThemeClass"
    @expanded="onWidgetExpand"
    @collapsed="isWidgetExpanded = false"
  >
    <template #header>
      <div :class="$style.headerContent">
        <span :class="$style.headerTitle">{{ headerTitle }}</span>
        <div
          v-if="partnerHeaderText && partnerHeaderLogo"
          :class="[$style.partnerHeader, isWidgetExpanded ? $style.expanded : $style.collapsed]"
        >
          <span>{{ partnerHeaderText }}</span>
          <img :class="$style.logo" :src="partnerHeaderLogo" :alt="id" />
        </div>
      </div>
    </template>
    <template v-if="isWidgetExpanded" #default>
      <div :class="$style.contentContainer">
        <slot name="component"></slot>
      </div>
      <div v-if="footerText && partnerLogo" :class="$style.footer">
        <span>{{ footerText }}</span>
        <HgImageLazyLoad :class="$style.logo" :src="partnerLogo" :alt="id" />
      </div>
    </template>
  </HgAccordion>
</template>

<script lang="ts" setup>
import { HgAccordion, HgImageLazyLoad } from '@smg-real-estate/domus';
import { onMounted, ref } from 'vue';

import { isPlatformIs24 } from '@/helpers/platformDetection';

defineOptions({
  name: 'PartnerCollapsibleWidget',
});

interface Props {
  id: string;
  headerTitle: string;
  footerText?: string;
  partnerLogo?: string;
  partnerHeaderText?: string;
  partnerHeaderLogo?: string;
}
const props = withDefaults(defineProps<Props>(), {
  footerText: '',
  partnerLogo: '',
  partnerHeaderText: '',
  partnerHeaderLogo: '',
});

const emit = defineEmits<{
  trackExpandWidget: [];
  mounted: [];
}>();

const isWidgetExpanded = ref(false);

onMounted(() => {
  emit('mounted');
});

const onWidgetExpand = (): void => {
  if (!isWidgetExpanded.value) {
    isWidgetExpanded.value = true;
    emit('trackExpandWidget');
  }
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

$logo-max-width: 6.25rem;
$logo-max-height: 2.5rem;
$accordion-header-is24-color: $color-azul-110;
$accordion-header-hg-color: $color-blue-60;

.accordionThemeClass {
  color: $accordion-header-hg-color;
  fill: $accordion-header-hg-color;
}

.IS24AccordionThemeClass {
  color: $accordion-header-is24-color;
  fill: $accordion-header-is24-color;
}

.headerContent {
  display: flex;
  flex-direction: column;
  gap: $space-50;
  align-items: flex-start;
  justify-content: center;
  color: $color-blue-60;

  @include font-bold;

  .headerTitle {
    display: block;
    text-align: left;
  }
}

.contentContainer {
  margin-bottom: $space-150;
}

.footer {
  display: flex;
  flex-direction: row;
  gap: $space-50;
  align-content: center;
  align-items: center;
  margin-bottom: $space-75;

  @include body-3($color-grey-90);
}

.partnerHeader {
  display: flex;
  flex-direction: row;
  gap: $space-50;
  align-content: center;
  align-items: center;

  &.expanded {
    margin-bottom: $space-25;
  }

  &.collapsed {
    margin-bottom: $space-75;
  }

  @include body-3($color-grey-90);
}

.logo {
  max-width: $logo-max-width;
  max-height: $logo-max-height;
}
</style>
